/**
 * Summary Generation Modal
 * 
 * Displays an animated 5-step progress modal while the reading session
 * summary is being generated. Polls the backend for status updates.
 */

import React, { useEffect, useState } from 'react';
import { summaryService, SessionSummaryStatusResponse } from '../services/summaryService';

interface SummaryGenerationModalProps {
  storySessionId: string;
  isOpen: boolean;
  onComplete: () => void;
  onError: (error: string) => void;
  onClose: () => void;
}

const GENERATION_STEPS = [
  {
    step: 1,
    title: 'Collecting Session Data',
    description: 'Gathering pronunciation, quiz, and reading progress...',
    icon: '📊',
    duration: 2000
  },
  {
    step: 2,
    title: 'Analyzing Pronunciation',
    description: 'Calculating accuracy scores and identifying patterns...',
    icon: '🎯',
    duration: 3000
  },
  {
    step: 3,
    title: 'Reviewing Quiz Performance',
    description: 'Evaluating comprehension and quiz results...',
    icon: '📝',
    duration: 2000
  },
  {
    step: 4,
    title: 'Generating Summary',
    description: 'Creating personalized feedback with AI...',
    icon: '✨',
    duration: 5000
  },
  {
    step: 5,
    title: 'Finalizing Report',
    description: 'Preparing your reading summary...',
    icon: '🎉',
    duration: 2000
  }
];

export default function SummaryGenerationModal({
  storySessionId,
  isOpen,
  onComplete,
  onError,
  onClose
}: SummaryGenerationModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [status, setStatus] = useState<SessionSummaryStatusResponse | null>(null);
  const [progressPercent, setProgressPercent] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;
    let pollTimer: NodeJS.Timeout;

    const pollStatus = async () => {
      try {
        const statusResponse = await summaryService.pollSummaryStatus(storySessionId);
        
        if (isCancelled) return;

        setStatus(statusResponse);

        // Update progress based on status
        if (statusResponse.progress_percent !== undefined) {
          setProgressPercent(statusResponse.progress_percent);
          
          // Map progress to steps
          if (statusResponse.progress_percent <= 20) setCurrentStep(0);
          else if (statusResponse.progress_percent <= 40) setCurrentStep(1);
          else if (statusResponse.progress_percent <= 60) setCurrentStep(2);
          else if (statusResponse.progress_percent <= 80) setCurrentStep(3);
          else setCurrentStep(4);
        } else {
          // Fallback: increment steps based on status message
          if (statusResponse.current_step?.includes('collect')) setCurrentStep(0);
          else if (statusResponse.current_step?.includes('rubric')) setCurrentStep(1);
          else if (statusResponse.current_step?.includes('quiz')) setCurrentStep(2);
          else if (statusResponse.current_step?.includes('generat')) setCurrentStep(3);
          else if (statusResponse.current_step?.includes('sav')) setCurrentStep(4);
        }

        // Check completion
        if (statusResponse.status === 'completed') {
          setProgressPercent(100);
          setCurrentStep(4);
          
          // Small delay before closing to show completion
          setTimeout(() => {
            if (!isCancelled) {
              onComplete();
            }
          }, 1000);
          return;
        }

        // Check for failure
        if (statusResponse.status === 'failed') {
          onError(statusResponse.error_message || 'Summary generation failed');
          return;
        }

        // Continue polling
        pollTimer = setTimeout(pollStatus, 2000);

      } catch (error: any) {
        if (!isCancelled) {
          console.error('Error polling summary status:', error);
          onError(error.message || 'Failed to check summary status');
        }
      }
    };

    // Start polling
    pollStatus();

    // Cleanup
    return () => {
      isCancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [isOpen, storySessionId, onComplete, onError]);

  if (!isOpen) return null;

  const currentStepData = GENERATION_STEPS[currentStep];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-3 animate-bounce">
            {currentStepData.icon}
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Generating Your Summary
          </h2>
          <p className="text-sm text-gray-600">
            This will just take a moment...
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-xs text-gray-500">
              Step {currentStep + 1} of {GENERATION_STEPS.length}
            </span>
            <span className="text-xs font-semibold text-purple-600">
              {progressPercent.toFixed(0)}%
            </span>
          </div>
        </div>

        {/* Current Step Info */}
        <div className="bg-purple-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-purple-900 mb-1 flex items-center gap-2">
            {currentStepData.icon}
            <span>{currentStepData.title}</span>
          </h3>
          <p className="text-sm text-purple-700">
            {status?.current_step || currentStepData.description}
          </p>
        </div>

        {/* Step Indicators */}
        <div className="flex justify-between mb-6">
          {GENERATION_STEPS.map((step, index) => (
            <div key={step.step} className="flex flex-col items-center flex-1">
              <div 
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                  transition-all duration-300 mb-1
                  ${index <= currentStep 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white scale-110' 
                    : 'bg-gray-200 text-gray-400'
                  }
                  ${index === currentStep ? 'ring-4 ring-purple-200' : ''}
                `}
              >
                {index < currentStep ? '✓' : step.step}
              </div>
              <span className="text-xs text-gray-500 text-center hidden sm:block">
                {step.icon}
              </span>
            </div>
          ))}
        </div>

        {/* Loading Animation */}
        <div className="flex justify-center items-center gap-2">
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>

        {/* Status Message */}
        {status && (
          <div className="mt-4 text-center text-xs text-gray-500">
            {status.message}
          </div>
        )}

        {/* Cancel Button (optional) */}
        <button
          onClick={onClose}
          className="mt-6 w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
